import { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, Briefcase, FileText, Calendar, Map,
  User, LogOut, ChevronLeft, ChevronRight, Sparkles,
  GraduationCap, Sun, Moon, GitCompare, Zap, History,
  Compass, Menu, X, Brain, BarChart3, Settings, PieChart,
  MessageCircle, Trophy, Flame, Crown, Shield, Calculator,
  ClipboardList, ChevronDown, Target, Mic, TrendingUp, Gift, Users, Linkedin,
  SlidersHorizontal, Building2
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { gamificationAPI } from '../lib/api';
import { useSubscription } from '../lib/subscription';

const NAV_GROUPS = [
  {
    id: 'main',
    items: [
      { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    ]
  },
  {
    id: 'intelligence',
    label: 'Career Intelligence',
    icon: Brain,
    items: [
      { path: '/career-intel', icon: BarChart3, label: 'Intelligence Dashboard' },
      { path: '/career-assessment', icon: Sparkles, label: 'Discovery' },
      { path: '/career-batteries', icon: Shield, label: 'Assessments' },
      { path: '/gap-analysis', icon: Target, label: 'Gap Analysis' },
      { path: '/career-report', icon: ClipboardList, label: 'Reports', pro: true },
      { path: '/career-tools', icon: Calculator, label: 'Tools', pro: true },
      { path: '/career-simulator', icon: SlidersHorizontal, label: 'Trajectory Simulator' },
      { path: '/company-intel', icon: Building2, label: 'Company Intel' },
      { path: '/linkedin-optimizer', icon: Linkedin, label: 'LinkedIn Optimizer' },
    ]
  },
  {
    id: 'studio',
    label: 'Studio',
    icon: FileText,
    items: [
      { path: '/resume', icon: FileText, label: 'Resume Studio' },
      { path: '/interview-prep', icon: Mic, label: 'Mock Interviews' },
    ]
  },
  {
    id: 'jobs',
    label: 'Job Center',
    icon: Briefcase,
    soon: true,
    items: [
      { path: '/jobs', icon: Briefcase, label: 'Job Search', soon: true },
      { path: '/compare', icon: GitCompare, label: 'Compare Jobs', soon: true },
      { path: '/future-skills', icon: Zap, label: 'Future Skills', soon: true },
    ]
  },
  {
    id: 'account',
    label: 'Account',
    icon: User,
    items: [
      { path: '/profile', icon: User, label: 'Profile' },
      { path: '/settings', icon: Settings, label: 'Settings' },
      { path: '/pricing', icon: Crown, label: 'Subscription' },
    ]
  },
  {
    id: 'more',
    label: 'More',
    icon: PieChart,
    items: [
      { path: '/community', icon: MessageCircle, label: 'Community' },
      { path: '/referral', icon: Gift, label: 'Refer & Earn' },
      { path: '/achievements', icon: Trophy, label: 'Achievements' },
      { path: '/analytics', icon: TrendingUp, label: 'Analytics', adminOnly: true },
    ]
  },
];

// HubSpot-style flyout panel for collapsed state
function FlyoutPanel({ group, isVisible, anchorTop }) {
  if (!isVisible) return null;
  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -8 }}
      transition={{ duration: 0.15 }}
      className="fixed z-[100] ml-1"
      style={{ top: anchorTop, left: 68 }}
    >
      <div className="rounded-xl py-2 px-1 min-w-[200px] shadow-2xl"
        style={{ background: '#253F78', border: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="px-3 py-1.5 mb-1">
          <span className="text-[10px] font-bold tracking-[0.15em] uppercase" style={{ color: 'rgba(255,255,255,0.4)' }}>
            {group.label}
          </span>
        </div>
        {group.items.map(item => (
          <NavLink key={item.path} to={item.path}
            data-testid={`flyout-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            className="flex items-center gap-2.5 px-3 py-2 mx-1 rounded-lg text-[13px] font-medium transition-all"
            style={({ isActive }) => ({
              background: isActive ? 'rgba(13,148,136,0.15)' : 'transparent',
              color: isActive ? '#14B8A6' : 'rgba(255,255,255,0.7)',
            })}>
            <item.icon className="w-4 h-4 flex-shrink-0" />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </div>
    </motion.div>
  );
}

export const Sidebar = ({ user, onLogout }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({ intelligence: true, jobs: true });
  const [hoveredGroup, setHoveredGroup] = useState(null);
  const [hoverAnchor, setHoverAnchor] = useState(0);
  const [gamProfile, setGamProfile] = useState(null);
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { isPro } = useSubscription();
  const hoverTimeoutRef = useRef(null);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  useEffect(() => {
    gamificationAPI.profile().then(res => setGamProfile(res.data)).catch(() => {});
  }, [location.pathname]);

  const toggleGroup = (id) => {
    setExpandedGroups(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const isGroupActive = (group) => {
    return group.items?.some(item => location.pathname === item.path);
  };

  const handleGroupHover = (groupId, e) => {
    if (!collapsed) return;
    clearTimeout(hoverTimeoutRef.current);
    const rect = e.currentTarget.getBoundingClientRect();
    setHoverAnchor(rect.top);
    setHoveredGroup(groupId);
  };

  const handleGroupLeave = () => {
    if (!collapsed) return;
    hoverTimeoutRef.current = setTimeout(() => setHoveredGroup(null), 200);
  };

  const handleFlyoutEnter = () => { clearTimeout(hoverTimeoutRef.current); };
  const handleFlyoutLeave = () => { setHoveredGroup(null); };

  // Render a single flat nav item (for top-level non-grouped items)
  const renderFlatItem = (item, isMobile) => {
    const isActive = location.pathname === item.path;
    return (
      <NavLink key={item.path} to={item.path}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] font-medium transition-all relative"
        style={{
          ...(isActive ? {
            background: 'rgba(13,148,136,0.12)',
            color: '#14B8A6',
          } : {
            color: 'rgba(247,250,252,0.6)',
          }),
        }}
        onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = '#F7FAFC'; }}}
        onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(247,250,252,0.6)'; }}}>
        {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full" style={{ background: '#0D9488' }} />}
        <item.icon className="w-[18px] h-[18px] flex-shrink-0" style={isActive ? { color: '#0D9488' } : { opacity: 0.7 }} />
        {(!collapsed || isMobile) && <span className="whitespace-nowrap">{item.label}</span>}
      </NavLink>
    );
  };

  // Render a grouped section (HubSpot-style)
  const renderGroup = (group, isMobile) => {
    const isExpanded = expandedGroups[group.id] ?? false;
    const isActive = isGroupActive(group);
    const GroupIcon = group.icon;

    if (collapsed && !isMobile) {
      // Collapsed: show only group icon with flyout on hover
      return (
        <div key={group.id} className="relative"
          onMouseEnter={(e) => handleGroupHover(group.id, e)}
          onMouseLeave={handleGroupLeave}>
          <button
            className="w-full flex items-center justify-center py-2.5 rounded-lg transition-all"
            style={{
              background: isActive ? 'rgba(13,148,136,0.12)' : hoveredGroup === group.id ? 'rgba(255,255,255,0.05)' : 'transparent',
              color: isActive ? '#14B8A6' : 'rgba(247,250,252,0.6)',
            }}
            data-testid={`nav-group-${group.id}`}>
            <GroupIcon className="w-[18px] h-[18px]" style={isActive ? { color: '#0D9488' } : { opacity: 0.7 }} />
          </button>
        </div>
      );
    }

    // Expanded: show group header with collapsible children
    return (
      <div key={group.id}>
        <button onClick={() => toggleGroup(group.id)}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-semibold transition-all"
          style={{
            color: isActive ? '#14B8A6' : 'rgba(247,250,252,0.5)',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          data-testid={`nav-group-${group.id}`}>
          <GroupIcon className="w-4 h-4 flex-shrink-0" style={isActive ? { color: '#0D9488' } : { opacity: 0.6 }} />
          <span className="flex-1 text-left text-[11px] font-bold tracking-[0.08em] uppercase">{group.label}</span>
          <ChevronDown className="w-3.5 h-3.5 transition-transform" style={{
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            opacity: 0.4,
          }} />
        </button>
        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden">
              <div className="pl-2 space-y-0.5 pb-1">
                {group.items.map(item => {
                  const itemActive = location.pathname === item.path;
                  return (
                    <NavLink key={item.path} to={item.path}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12px] font-medium transition-all relative"
                      style={{
                        ...(itemActive ? {
                          background: 'rgba(13,148,136,0.12)',
                          color: '#14B8A6',
                        } : {
                          color: 'rgba(247,250,252,0.55)',
                        }),
                      }}
                      onMouseEnter={e => { if (!itemActive) { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = '#F7FAFC'; }}}
                      onMouseLeave={e => { if (!itemActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(247,250,252,0.55)'; }}}>
                      {itemActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 rounded-r-full" style={{ background: '#0D9488' }} />}
                      <item.icon className="w-4 h-4 flex-shrink-0" style={itemActive ? { color: '#0D9488' } : { opacity: 0.5 }} />
                      <span className="whitespace-nowrap">{item.label}</span>
                      {item.soon && (
                        <span className="ml-auto text-[8px] px-1.5 py-0.5 rounded-full font-bold uppercase"
                          style={{ background: 'rgba(245,132,31,0.15)', color: '#F5841F' }}>
                          Soon
                        </span>
                      )}
                      {item.pro && !isPro && !item.soon && (
                        <span className="ml-auto text-[8px] px-1.5 py-0.5 rounded-full font-bold"
                          style={{ background: 'rgba(13,148,136,0.15)', color: '#14B8A6' }}
                          data-testid={`sidebar-pro-badge-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
                          PRO
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const sidebarContent = (isMobile) => (
    <>
      {/* Brand */}
      <div className="flex items-center justify-center px-3 h-16 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <img src={collapsed && !isMobile ? "/kairera-white-mono.png" : "/kairera-white.png"}
          alt="Kairera"
          className="flex-shrink-0"
          style={{ height: collapsed && !isMobile ? 36 : 45 }}
          data-testid="sidebar-logo" />
        {isMobile && (
          <button onClick={() => setMobileOpen(false)} className="ml-auto p-1.5 rounded-lg"
            style={{ color: 'rgba(255,255,255,0.5)' }} data-testid="mobile-sidebar-close">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* XP Bar */}
      {gamProfile && (!collapsed || isMobile) && (
        <div className="px-3 py-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }} data-testid="xp-bar">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-1.5">
              <Trophy className="w-3 h-3" style={{ color: '#F5841F' }} />
              <span className="text-[10px] font-bold" style={{ color: '#F5841F' }}>Lv.{gamProfile.level}</span>
            </div>
            <span className="text-[9px]" style={{ color: 'rgba(255,255,255,0.3)' }}>{gamProfile.total_xp} XP</span>
          </div>
          <div className="h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
            <div className="h-full rounded-full" style={{ width: `${gamProfile.level_progress}%`, background: 'linear-gradient(90deg, #0D9488, #14B8A6)' }} />
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 py-2 px-2 overflow-y-auto" style={{ scrollbarWidth: 'none' }}>
        <div className="space-y-1">
          {NAV_GROUPS.map(group => {
            if (!group.label) {
              // Flat items (Dashboard)
              return group.items.map(item => renderFlatItem(item, isMobile));
            }
            return renderGroup(group, isMobile);
          })}
        </div>
      </nav>

      {/* Footer */}
      <div className="flex-shrink-0 px-2 py-2 space-y-1" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        {/* Theme toggle */}
        <button data-testid="theme-toggle" onClick={toggleTheme}
          className="flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-[12px] font-medium transition-colors"
          style={{ color: 'rgba(247,250,252,0.5)' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
          {theme === 'dark'
            ? <Sun className="w-4 h-4 flex-shrink-0" style={{ color: '#F5841F' }} />
            : <Moon className="w-4 h-4 flex-shrink-0" style={{ color: '#9DB5DC' }} />}
          {(!collapsed || isMobile) && <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>}
        </button>

        {/* Settings */}
        <NavLink to="/settings" data-testid="nav-settings"
          className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12px] font-medium transition-colors"
          style={{ color: 'rgba(247,250,252,0.5)' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
          <Settings className="w-4 h-4 flex-shrink-0" style={{ opacity: 0.6 }} />
          {(!collapsed || isMobile) && <span>Settings</span>}
        </NavLink>

        {/* User */}
        {(!collapsed || isMobile) && (
          <div className="flex items-center gap-2.5 px-2 py-2">
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0"
              style={{ background: 'rgba(13,148,136,0.15)', color: '#14B8A6' }}>
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-semibold truncate" style={{ color: '#F7FAFC' }}>{user?.name || 'User'}</p>
              <p className="text-[9px] truncate" style={{ color: 'rgba(255,255,255,0.35)' }}>{user?.email || ''}</p>
            </div>
          </div>
        )}

        {/* Logout + collapse */}
        <div className="flex items-center gap-1">
          <button data-testid="logout-button" onClick={onLogout}
            className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[11px] font-medium transition-colors"
            style={{ color: 'rgba(255,255,255,0.35)' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(220,38,38,0.1)'; e.currentTarget.style.color = '#FC8181'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.35)'; }}>
            <LogOut className="w-3.5 h-3.5" />
            {(!collapsed || isMobile) && <span>Sign Out</span>}
          </button>
          {!isMobile && (
            <button data-testid="sidebar-toggle" onClick={() => setCollapsed(!collapsed)}
              className="p-1.5 rounded-lg transition-colors"
              style={{ color: 'rgba(255,255,255,0.35)' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
              {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile hamburger */}
      <button
        data-testid="mobile-menu-btn"
        onClick={() => setMobileOpen(true)}
        className="fixed top-3 left-3 z-[60] p-2 rounded-lg lg:hidden"
        style={{ background: 'var(--t-bg-card-solid)', border: '1px solid var(--t-border)', color: 'var(--t-text)' }}>
        <Menu className="w-5 h-5" />
      </button>

      {/* Mobile overlay + sidebar */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm lg:hidden"
              onClick={() => setMobileOpen(false)} />
            <motion.aside
              initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }}
              transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
              className="fixed top-0 left-0 h-screen w-[260px] flex flex-col z-[80] lg:hidden"
              style={{ background: '#1B3461' }}>
              {sidebarContent(true)}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Desktop sidebar */}
      <motion.aside
        data-testid="sidebar"
        initial={false}
        animate={{ width: collapsed ? 60 : 250 }}
        transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
        className="h-screen sticky top-0 flex-col z-50 hidden lg:flex"
        style={{ background: '#1B3461' }}>
        {sidebarContent(false)}
      </motion.aside>

      {/* Flyout panels for collapsed state */}
      <AnimatePresence>
        {collapsed && hoveredGroup && (
          <div onMouseEnter={handleFlyoutEnter} onMouseLeave={handleFlyoutLeave}>
            <FlyoutPanel
              group={NAV_GROUPS.find(g => g.id === hoveredGroup)}
              isVisible={true}
              anchorTop={hoverAnchor}
            />
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
